// src/pages/harvesting/monitor-hasil-panen/MonitorHasilPanen.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatDateDisplay,
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import fungsi perhitungan
import {
    calculateAllFormulas
} from "../formula/Calculation";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";
// Import fungsi import Excel
import { importExcelToFirebase, refreshMonitorData } from "../utils/excelImportHandler";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

import LokasiClosingByMouth from "./LokasiClosingByMouth";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Ambil karakter ke-6 dari bibit
const getBibitType = (bibit) => {
    if (!bibit || bibit === '') {
        return 'Ratoon';
    }
    
    const bibitString = String(bibit);
    if (bibitString.length >= 6) {
        const sixthChar = bibitString.charAt(5); // Index 5 = karakter ke-6
        switch (sixthChar.toUpperCase()) {
            case 'S':
                return 'SC';
            case 'C':
                return 'CR';
            case 'N':
                return 'AN';
            default:
                return bibitString;
        }
    }
    return bibitString;
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find(lokasi => lokasi.id_lokasi === id_lokasi);
};

// Helper: Ambil nama kepala kebun plant care berdasarkan lokasi
const getKepalaKebunPlantCare = async (lokasiName, database, onValuePromise) => {
    try {
        // Cari data pj_wilayah berdasarkan lokasi
        const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
        const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
        const pjWilayahData = pjWilayahSnapshot.val();
        
        if (!pjWilayahData) return null;
        
        // Cari key yang sesuai dengan lokasi
        let foundIdKepalaKebunPlantCare = null;
        
        Object.keys(pjWilayahData).forEach(key => {
            const wilayah = pjWilayahData[key];
            if (wilayah.lokasi === lokasiName) {
                foundIdKepalaKebunPlantCare = wilayah.id_kepala_kebun_plant_care;
            }
        });
        
        if (!foundIdKepalaKebunPlantCare) return null;
        
        // Cari nama kepala kebun plant care berdasarkan ID
        const kepalaKebunRef = ref(database, `db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care/${foundIdKepalaKebunPlantCare}`);
        const kepalaKebunSnapshot = await onValuePromise(kepalaKebunRef);
        const kepalaKebunData = kepalaKebunSnapshot.val();
        
        return kepalaKebunData ? kepalaKebunData.nama_kepala_kebun_plant_care : null;
        
    } catch (error) {
        console.error("Error fetching kepala kebun plant care:", error);
        return null;
    }
};

// Helper: Ambil nama kepala wilayah berdasarkan lokasi
const getKepalaWilayah = async (lokasiName, database, onValuePromise) => {
    try {
        // Cari data pj_wilayah berdasarkan lokasi
        const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
        const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
        const pjWilayahData = pjWilayahSnapshot.val();
        
        if (!pjWilayahData) return null;
        
        // Cari key yang sesuai dengan lokasi
        let foundIdKepalaWilayah = null;
        
        Object.keys(pjWilayahData).forEach(key => {
            const wilayah = pjWilayahData[key];
            if (wilayah.lokasi === lokasiName) {
                foundIdKepalaWilayah = wilayah.id_kepala_wilayah;
            }
        });
        
        if (!foundIdKepalaWilayah) return null;
        
        // Cari nama kepala wilayah berdasarkan ID
        const kepalaWilayahRef = ref(database, `db_config/db_fresh_pineapple/tb_kepala_wilayah/${foundIdKepalaWilayah}`);
        const kepalaWilayahSnapshot = await onValuePromise(kepalaWilayahRef);
        const kepalaWilayahData = kepalaWilayahSnapshot.val();
        
        return kepalaWilayahData ? kepalaWilayahData.nama_kepala_wilayah : null;
        
    } catch (error) {
        console.error("Error fetching kepala wilayah:", error);
        return null;
    }
};

function MonitorHasilPanen() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk data konfigurasi dropdown
    const [kodeWilayahList, setKodeWilayahList] = useState([]);
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [lokasiData, setLokasiData] = useState([]); // Array data dari db_freshpine/tb_lokasi
    const [kepalaKebunPlantCareCache, setKepalaKebunPlantCareCache] = useState({}); // Cache untuk nama kepala kebun
    const [kepalaWilayahCache, setKepalaWilayahCache] = useState({}); // Cache untuk nama kepala wilayah
    
    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterTahun, setFilterTahun] = useState('');

    // Fungsi untuk mengambil nama kepala kebun plant care
    const fetchKepalaKebunPlantCare = async (lokasiName) => {
        // Cek cache dulu
        if (kepalaKebunPlantCareCache[lokasiName]) {
            return kepalaKebunPlantCareCache[lokasiName];
        }
        
        // Jika tidak ada di cache, fetch dari Firebase
        const namaKepalaKebun = await getKepalaKebunPlantCare(lokasiName, database, onValuePromise);
        
        // Update cache
        if (namaKepalaKebun) {
            setKepalaKebunPlantCareCache(prev => ({
                ...prev,
                [lokasiName]: namaKepalaKebun
            }));
        }
        
        return namaKepalaKebun;
    };

    // Fungsi untuk mengambil nama kepala wilayah
    const fetchKepalaWilayah = async (lokasiName) => {
        // Cek cache dulu
        if (kepalaWilayahCache[lokasiName]) {
            return kepalaWilayahCache[lokasiName];
        }
        
        // Jika tidak ada di cache, fetch dari Firebase
        const namaKepalaWilayah = await getKepalaWilayah(lokasiName, database, onValuePromise);
        
        // Update cache
        if (namaKepalaWilayah) {
            setKepalaWilayahCache(prev => ({
                ...prev,
                [lokasiName]: namaKepalaWilayah
            }));
        }
        
        return namaKepalaWilayah;
    };

    // Fungsi untuk mengambil semua data terkait lokasi (kepala kebun plant care dan kepala wilayah)
    const fetchLokasiData = async (lokasiName) => {
        const [kepalaKebunPlantCare, kepalaWilayah] = await Promise.all([
            fetchKepalaKebunPlantCare(lokasiName),
            fetchKepalaWilayah(lokasiName)
        ]);
        
        return {
            kepalaKebunPlantCare,
            kepalaWilayah,
            isKepalaKebunPlantCare: !!kepalaKebunPlantCare,
            isKepalaWilayahFromPJ: !!kepalaWilayah
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_harvesting/tb_monitor_hasil_panen
                const monitorRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
                const monitorSnapshot = await onValuePromise(monitorRef);
                const monitorData = monitorSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (monitorData) {
                    Object.keys(monitorData).forEach(key => {
                        const record = monitorData[key];
                        // HANYA ambil data dari Excel yang disimpan di Firebase
                        loadedData.push({ 
                            firebaseKey: key, 
                            // Hanya data dari Excel yang disimpan di Firebase
                            id_lokasi: record.id_lokasi || '',
                            tanggal_closing: record.tanggal_closing || '-',
                            hsf: record.hsf || '-',
                            target_panen: record.target_panen || '-',
                            real_panen: record.real_panen || '-',
                            ritase: record.ritase || '-',
                            ph21: record.ph21 || '-',
                            ph22: record.ph22 || '-',
                            pnfpg4: record.pnfpg4 || '-',
                            daily: record.daily || '-',
                            budget: record.budget || '-', // Tambahkan budget
                        });
                        
                        // Cari waktu update terakhir dari last_imported_at
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Fetch data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();
                
                let lokasiArray = [];
                if (lokasiRawData) {
                    // Konversi dari object Firebase ke array
                    lokasiArray = Object.keys(lokasiRawData).map(key => ({
                        firebaseKey: key,
                        ...lokasiRawData[key]
                    }));
                    setLokasiData(lokasiArray);
                    
                    // Update dataList dengan informasi dari tb_lokasi dan perhitungan formula
                    const updatedDataList = await Promise.all(loadedData.map(async (item) => {
                        const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);
                        
                        let baseData = {};
                        
                        if (lokasiInfo) {
                            // Ambil data dari tb_lokasi
                            const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                            const displayLuas = lokasiInfo.netto || '-';
                            const displayStatus = lokasiInfo.status || '';
                            const rawBibit = lokasiInfo.bibit || '';
                            const displayJenisBibit = getBibitType(rawBibit);
                            
                            // Default kepala kebun dan kepala wilayah dari tb_lokasi
                            const defaultKepalaKebun = lokasiInfo.kepala_kebun || '';
                            const defaultKepalaWilayah = lokasiInfo.kepala_wilayah || '';
                            
                            // Coba ambil kepala kebun plant care dan kepala wilayah dari tb_pj_wilayah
                            const lokasiAdditionalData = displayLokasi ? 
                                await fetchLokasiData(displayLokasi) : 
                                { 
                                    kepalaKebunPlantCare: null, 
                                    kepalaWilayah: null,
                                    isKepalaKebunPlantCare: false,
                                    isKepalaWilayahFromPJ: false
                                };
                            
                            // Gunakan kepala kebun plant care jika ada, jika tidak gunakan default dari tb_lokasi
                            const finalKepalaKebun = lokasiAdditionalData.kepalaKebunPlantCare || defaultKepalaKebun;
                            
                            // Gunakan kepala wilayah dari tb_pj_wilayah jika ada, jika tidak gunakan default dari tb_lokasi
                            const finalKepalaWilayah = lokasiAdditionalData.kepalaWilayah || defaultKepalaWilayah;
                            
                            baseData = {
                                ...item,
                                // Data dari tb_lokasi
                                lokasi: displayLokasi,
                                luas: displayLuas,
                                status: displayStatus,
                                jenis_bibit: displayJenisBibit,
                                // Prioritas: kepala kebun plant care, lalu default dari tb_lokasi
                                kepala_kebun: finalKepalaKebun,
                                // Prioritas: kepala wilayah dari tb_pj_wilayah, lalu default dari tb_lokasi
                                kepala_wilayah: finalKepalaWilayah,
                                tahun: lokasiInfo.tahun || '',
                                // Tambahkan flag untuk identifikasi
                                isKepalaKebunPlantCare: lokasiAdditionalData.isKepalaKebunPlantCare,
                                isKepalaWilayahFromPJ: lokasiAdditionalData.isKepalaWilayahFromPJ
                            };
                        } else {
                            // Jika tidak ditemukan di tb_lokasi, coba ambil kepala kebun plant care dan kepala wilayah berdasarkan id_lokasi
                            const displayLokasi = item.id_lokasi || '';
                            const lokasiAdditionalData = displayLokasi ? 
                                await fetchLokasiData(displayLokasi) : 
                                { 
                                    kepalaKebunPlantCare: null, 
                                    kepalaWilayah: null,
                                    isKepalaKebunPlantCare: false,
                                    isKepalaWilayahFromPJ: false
                                };
                            
                            baseData = {
                                ...item,
                                lokasi: displayLokasi,
                                luas: '-',
                                status: '',
                                jenis_bibit: '',
                                kepala_kebun: lokasiAdditionalData.kepalaKebunPlantCare || '',
                                kepala_wilayah: lokasiAdditionalData.kepalaWilayah || '',
                                tahun: '',
                                isKepalaKebunPlantCare: lokasiAdditionalData.isKepalaKebunPlantCare,
                                isKepalaWilayahFromPJ: lokasiAdditionalData.isKepalaWilayahFromPJ
                            };
                        }
                        
                        // Hitung formula
                        const calculatedData = calculateAllFormulas({
                            tanggal_closing: baseData.tanggal_closing,
                            target_panen: baseData.target_panen,
                            real_panen: baseData.real_panen,
                            luas: baseData.luas,
                            ph21: baseData.ph21,
                            ph22: baseData.ph22,
                            pnfpg4: baseData.pnfpg4,
                            daily: baseData.daily
                        });
                        
                        // Gabungkan data dasar dengan hasil perhitungan
                        return {
                            ...baseData,
                            ...calculatedData
                        };
                    }));
                    
                    setOriginalDataList(updatedDataList);
                    setDataList(updatedDataList);
                } else {
                    // Jika tb_lokasi kosong, gunakan data dasar
                    const basicDataList = await Promise.all(loadedData.map(async (item) => {
                        const displayLokasi = item.id_lokasi || '';
                        const lokasiAdditionalData = displayLokasi ? 
                            await fetchLokasiData(displayLokasi) : 
                            { 
                                kepalaKebunPlantCare: null, 
                                kepalaWilayah: null,
                                isKepalaKebunPlantCare: false,
                                isKepalaWilayahFromPJ: false
                            };
                        
                        const baseData = {
                            ...item,
                            lokasi: displayLokasi,
                            luas: '-',
                            status: '',
                            jenis_bibit: '',
                            kepala_kebun: lokasiAdditionalData.kepalaKebunPlantCare || '',
                            kepala_wilayah: lokasiAdditionalData.kepalaWilayah || '',
                            tahun: '',
                            isKepalaKebunPlantCare: lokasiAdditionalData.isKepalaKebunPlantCare,
                            isKepalaWilayahFromPJ: lokasiAdditionalData.isKepalaWilayahFromPJ
                        };
                        
                        // Hitung formula
                        const calculatedData = calculateAllFormulas({
                            tanggal_closing: baseData.tanggal_closing,
                            target_panen: baseData.target_panen,
                            real_panen: baseData.real_panen,
                            luas: baseData.luas
                        });
                        
                        return {
                            ...baseData,
                            ...calculatedData
                        };
                    }));
                    
                    setOriginalDataList(basicDataList);
                    setDataList(basicDataList);
                    setLokasiData([]);
                }

                // Fetch lookup data for Kode Wilayah
                const kodeWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kode_wilayah");
                const kodeWilayahConfigSnapshot = await onValuePromise(kodeWilayahConfigRef);
                const kodeWilayahConfigData = kodeWilayahConfigSnapshot.val();
                if (kodeWilayahConfigData) {
                    const list = Object.keys(kodeWilayahConfigData).map(key => ({
                        id: key,
                        nama_kode_wilayah: kodeWilayahConfigData[key].nama_kode_wilayah,
                    }));
                    setKodeWilayahList(list);
                }

                // Fetch lookup data for Status Lokasi (untuk warna indicator)
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.lokasi && item.lokasi.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            const updatedData = await refreshMonitorData(database, onValuePromise);
            
            // Update dengan data dari tb_lokasi jika tersedia dan hitung formula
            const finalData = await Promise.all(updatedData.map(async (item) => {
                const lokasiInfo = findLokasiById(lokasiData, item.id_lokasi);
                
                let baseData = {};
                
                if (lokasiInfo) {
                    // Ambil data dari tb_lokasi
                    const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                    const displayLuas = lokasiInfo.netto || '-';
                    const displayStatus = lokasiInfo.status || '';
                    const rawBibit = lokasiInfo.bibit || '';
                    const displayJenisBibit = getBibitType(rawBibit);
                    
                    // Default kepala kebun dan kepala wilayah dari tb_lokasi
                    const defaultKepalaKebun = lokasiInfo.kepala_kebun || '';
                    const defaultKepalaWilayah = lokasiInfo.kepala_wilayah || '';
                    
                    // Coba ambil kepala kebun plant care dan kepala wilayah dari tb_pj_wilayah
                    const lokasiAdditionalData = displayLokasi ? 
                        await fetchLokasiData(displayLokasi) : 
                        { 
                            kepalaKebunPlantCare: null, 
                            kepalaWilayah: null,
                            isKepalaKebunPlantCare: false,
                            isKepalaWilayahFromPJ: false
                        };
                    
                    // Gunakan kepala kebun plant care jika ada, jika tidak gunakan default dari tb_lokasi
                    const finalKepalaKebun = lokasiAdditionalData.kepalaKebunPlantCare || defaultKepalaKebun;
                    
                    // Gunakan kepala wilayah dari tb_pj_wilayah jika ada, jika tidak gunakan default dari tb_lokasi
                    const finalKepalaWilayah = lokasiAdditionalData.kepalaWilayah || defaultKepalaWilayah;
                    
                    baseData = {
                        ...item,
                        // Data dari tb_lokasi
                        lokasi: displayLokasi,
                        luas: displayLuas,
                        status: displayStatus,
                        jenis_bibit: displayJenisBibit,
                        // Prioritas: kepala kebun plant care, lalu default dari tb_lokasi
                        kepala_kebun: finalKepalaKebun,
                        // Prioritas: kepala wilayah dari tb_pj_wilayah, lalu default dari tb_lokasi
                        kepala_wilayah: finalKepalaWilayah,
                        tahun: lokasiInfo.tahun || '',
                        isKepalaKebunPlantCare: lokasiAdditionalData.isKepalaKebunPlantCare,
                        isKepalaWilayahFromPJ: lokasiAdditionalData.isKepalaWilayahFromPJ
                    };
                } else {
                    // Jika tidak ditemukan di tb_lokasi, coba ambil kepala kebun plant care dan kepala wilayah berdasarkan id_lokasi
                    const displayLokasi = item.id_lokasi || '';
                    const lokasiAdditionalData = displayLokasi ? 
                        await fetchLokasiData(displayLokasi) : 
                        { 
                            kepalaKebunPlantCare: null, 
                            kepalaWilayah: null,
                            isKepalaKebunPlantCare: false,
                            isKepalaWilayahFromPJ: false
                        };
                    
                    baseData = {
                        ...item,
                        lokasi: displayLokasi,
                        luas: '-',
                        status: '',
                        jenis_bibit: '',
                        kepala_kebun: lokasiAdditionalData.kepalaKebunPlantCare || '',
                        kepala_wilayah: lokasiAdditionalData.kepalaWilayah || '',
                        tahun: '',
                        isKepalaKebunPlantCare: lokasiAdditionalData.isKepalaKebunPlantCare,
                        isKepalaWilayahFromPJ: lokasiAdditionalData.isKepalaWilayahFromPJ
                    };
                }
                
                // Hitung formula
                const calculatedData = calculateAllFormulas({
                    tanggal_closing: baseData.tanggal_closing,
                    target_panen: baseData.target_panen,
                    real_panen: baseData.real_panen,
                    luas: baseData.luas
                });
                
                return {
                    ...baseData,
                    ...calculatedData
                };
            }));
            
            return finalData;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import dari file terpisah
            const result = await importExcelToFirebase(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui.`,
                    severity: "success",
                });
                
                // Set last updated time
                setLastUpdated(new Date());
                
                // Refresh data setelah import berhasil dengan logika kepala kebun plant care
                const finalData = await refreshDataAfterImport();
                
                setDataList(finalData);
                setOriginalDataList(finalData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterStatusLokasi) {
            filtered = filtered.filter(item => item.status === filterStatusLokasi);
        }
        if (filterTahun) {
            filtered = filtered.filter(item => item.tahun === filterTahun);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterTahun('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Get unique years for filter dropdown
    const tahunList = [...new Set(originalDataList.map(item => item.tahun))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/*<LokasiClosingByMouth/>*/}

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tahun</InputLabel>
                            <Select
                                value={filterTahun}
                                label="Tahun"
                                onChange={(e) => setFilterTahun(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {tahunList.map((tahun) => (
                                    <MenuItem key={tahun} value={tahun}>
                                        {tahun}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Data Monitor Hasil Panen
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari berdasarkan lokasi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="monitor hasil panen table">
                                <TableHead>
                                    {/* Baris Pertama - Header Utama */}
                                    <TableRow>
                                        {/* Info Lokasi - 6 kolom */}
                                        <TableCell colSpan={6} className="ded-tch">
                                            Info Lokasi
                                        </TableCell>
                                        
                                        {/* Produksi Panen - 9 kolom */}
                                        <TableCell colSpan={9} className="ded-tch">
                                            Produksi Panen
                                        </TableCell>
                                        
                                        {/* Packing House - 5 kolom */}
                                        <TableCell colSpan={5} className="ded-tch">
                                            Packing House
                                        </TableCell>
                                        
                                        {/* Daily Monitoring - 2 kolom */}
                                        <TableCell colSpan={2} className="ded-tch">
                                            Daily Monitoring
                                        </TableCell>
                                        
                                        {/* Budget - 1 kolom (ditambahkan di akhir) */}
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Budget
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Baris Kedua - Subheaders */}
                                    <TableRow className="tr">
                                        {/* Info Lokasi Subheaders (6 kolom) */}
                                        <TableCell className="ded-tc">Lokasi</TableCell>
                                        <TableCell className="ded-tc">Luas</TableCell>
                                        <TableCell className="ded-tc">Status</TableCell>
                                        <TableCell className="ded-tc">Jenis Bibit</TableCell>
                                        <TableCell className="ded-tc">
                                            Kepala Kebun 
                                        </TableCell>
                                        <TableCell className="ded-tc">
                                            Kepala Wilayah
                                        </TableCell>
                                        
                                        {/* Produksi Panen Subheaders (9 kolom) */}
                                        <TableCell className="ded-tc">Tanggal Closing</TableCell>
                                        <TableCell className="ded-tc">HSF</TableCell>
                                        <TableCell className="ded-tc">Target Panen <br/>(Ton)</TableCell>
                                        <TableCell className="ded-tc">Real Panen <br/>(Ton)</TableCell>
                                        <TableCell className="ded-tc">Pencapaian Panen <br/>(%)</TableCell>
                                        <TableCell className="ded-tc">Luas Terpanen</TableCell>
                                        <TableCell className="ded-tc">Yield <br/>(Ton/Ha)</TableCell>
                                        <TableCell className="ded-tc">Var Yield Renc. VS Real <br/>(%)</TableCell>
                                        <TableCell className="ded-tc">Ritase</TableCell>
                                        
                                        {/* Packing House Subheaders (5 kolom) */}
                                        <TableCell className="ded-tc">Rencana Yield <br/>(Ton/Ha)</TableCell>
                                        <TableCell className="ded-tc">PH21 <br/>(KG)</TableCell>
                                        <TableCell className="ded-tc">PH22 <br/>(KG)</TableCell>
                                        <TableCell className="ded-tc">PNFPG4</TableCell>
                                        <TableCell className="ded-tc">Total Grade Tanpa Crown (KG)</TableCell>
                                        
                                        {/* Daily Monitoring Subheaders (2 kolom) */}
                                        <TableCell className="ded-tc">Daily</TableCell>
                                        <TableCell className="ded-tc">Selisih</TableCell>
                                        
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            // Lookup nama_status_lokasi dan indicator dari config
                                            const statusLokasiItem = statusLokasiList.find(
                                                (item) => item.nama_status_lokasi === row.status
                                            );
                                            const displayStatusLokasi = row.status || '';
                                            const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                                            return (
                                                <TableRow key={row.firebaseKey}>
                                                    {/* Info Lokasi - 6 kolom */}
                                                    <TableCell>
                                                        <Box>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                {formatText(row.lokasi)}
                                                            </div>
                                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                                ID: {row.id_lokasi || '-'}
                                                            </div>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{row.luas}</TableCell>
                                                    <TableCell>
                                                        {displayStatusLokasi ? (
                                                            <Chip
                                                                label={displayStatusLokasi}
                                                                style={{ 
                                                                    backgroundColor: statusIndicatorColor, 
                                                                    color: theme.palette.getContrastText(statusIndicatorColor) 
                                                                }}
                                                                size="small"
                                                            />
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell>{formatText(row.jenis_bibit)}</TableCell>
                                                    <TableCell>
                                                        {formatText(row.kepala_kebun)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatText(row.kepala_wilayah)}
                                                    </TableCell>
                                                    
                                                    {/* Produksi Panen - 9 kolom */}
                                                    <TableCell>{formatDateDisplay(row.tanggal_closing)}</TableCell>
                                                    <TableCell>{formatNumber(row.hsf)}</TableCell>
                                                    <TableCell>{formatDecimal(row.target_panen)}</TableCell>
                                                    <TableCell>{formatDecimal(row.real_panen)}</TableCell>
                                                    <TableCell>{row.pencapaian_panen}</TableCell>
                                                    <TableCell>{row.luas_terpanen}</TableCell>
                                                    <TableCell>{row.yield}</TableCell>
                                                    <TableCell>{row.var_yield_renc_vs_real}</TableCell>
                                                    <TableCell>{formatNumber(row.ritase)}</TableCell>
                                                    
                                                    {/* Packing House - 5 kolom */}
                                                    <TableCell>{row.rencana_yield}</TableCell>
                                                    <TableCell>{formatDecimal(row.ph21)}</TableCell>
                                                    <TableCell>{formatDecimal(row.ph22)}</TableCell>
                                                    <TableCell>{formatDecimal(row.pnfpg4)}</TableCell>
                                                    <TableCell>{formatDecimal(row.grade_tanpa_crown)}</TableCell>
                                                    
                                                    {/* Daily Monitoring - 2 kolom */}
                                                    <TableCell>{formatDecimal(row.daily)}</TableCell>
                                                    <TableCell>{row.selisih}</TableCell>
                                                    
                                                    {/* Budget - 1 kolom */}
                                                    <TableCell>{formatDecimal(row.budget)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data monitor hasil panen.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MonitorHasilPanen;